import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import {
    applyCustomiseLabelsRules,
    customiseLabelsOptions,
    getCustomiseLabelsDisabledOptions,
    TCustomiseLabelsDisabledOptions,
    TCustomiseLabelsOptions,
} from "components/Charts/components/CustomiseLabels";

type TenureLabelsContextValue = {
    customiseLabels: TCustomiseLabelsOptions;
    disabledOptions: TCustomiseLabelsDisabledOptions;
    setCustomiseLabels: (options: TCustomiseLabelsOptions) => void;
};

const initialCustomiseLabels = customiseLabelsOptions
    .filter((option) => option.id === "show-names" || option.id === "show-roles")
    .reduce((acc, option) => {
        acc[option.id] = true;
        return acc;
    }, {} as TCustomiseLabelsOptions);

const TenureLabelsContext = createContext<TenureLabelsContextValue | null>(null);

export const TenureLabelsProvider = ({ children }: { children: ReactNode }) => {
    const [customiseLabels, setCustomiseLabelsState] = useState<TCustomiseLabelsOptions>(initialCustomiseLabels);

    const setCustomiseLabels = useCallback((options: TCustomiseLabelsOptions) => {
        setCustomiseLabelsState(applyCustomiseLabelsRules(options));
    }, []);

    const disabledOptions = useMemo(() => getCustomiseLabelsDisabledOptions(customiseLabels), [customiseLabels]);

    const value = useMemo(
        () => ({
            customiseLabels,
            disabledOptions,
            setCustomiseLabels,
        }),
        [customiseLabels, disabledOptions, setCustomiseLabels]
    );

    return <TenureLabelsContext.Provider value={value}>{children}</TenureLabelsContext.Provider>;
};

export const useTenureLabelsContext = () => {
    const context = useContext(TenureLabelsContext);

    if (!context) {
        throw new Error("useTenureLabelsContext must be used inside TenureLabelsProvider");
    }

    return context;
};
